import { SESSION_KEYS } from './exitIntentSavePrompt.constants';
import { UnsavedViewRecord } from './exitIntentSavePrompt.types';

/**
 * Guarded sessionStorage wrapper (LAAC-2038 AC 27 — sessionStorage unavailable/privacy mode must never throw).
 * Falls back to an in-memory map for the current page lifecycle when sessionStorage read/write throws.
 * No `useSessionStorage` hook exists in this codebase to reuse, and the spec is explicit that this
 * feature's frequency-model flags must be sessionStorage-scoped (not localStorage), per Sections 5/6/8.
 */
let inMemoryFallback = new Map<string, string>();

/**
 * Test-only: resets the in-memory fallback so it mirrors a fresh session, matching how a real
 * page load resets it (LAAC-2038 spec Section 8). Called from the storage spec's `beforeEach` alongside
 * `sessionStorage.clear()` so the two "new session" resets stay in sync across tests.
 */
export const __resetExitIntentSaveInMemoryFallbackForTests = () => {
    inMemoryFallback = new Map<string, string>();
};

const safeGetItem = (key: string): string | null => {
    try {
        return sessionStorage.getItem(key);
    } catch {
        return inMemoryFallback.has(key) ? inMemoryFallback.get(key)! : null;
    }
};

const safeSetItem = (key: string, value: string): void => {
    try {
        sessionStorage.setItem(key, value);
    } catch {
        inMemoryFallback.set(key, value);
    }
};

const safeRemoveItem = (key: string): void => {
    try {
        sessionStorage.removeItem(key);
    } catch {
        inMemoryFallback.delete(key);
    }
};

export const readPending = (): UnsavedViewRecord | null => {
    const raw = safeGetItem(SESSION_KEYS.pending);
    if (!raw) {
        return null;
    }

    try {
        return JSON.parse(raw) as UnsavedViewRecord;
    } catch {
        return null;
    }
};

export const writePending = (record: UnsavedViewRecord): void => {
    safeSetItem(SESSION_KEYS.pending, JSON.stringify(record));
};

export const clearPending = (): void => {
    safeRemoveItem(SESSION_KEYS.pending);
};

export const getUserSaved = (): boolean => safeGetItem(SESSION_KEYS.userSaved) === 'true';

export const setUserSaved = (): void => {
    safeSetItem(SESSION_KEYS.userSaved, 'true');
};

export const getUserDismissed = (): boolean => safeGetItem(SESSION_KEYS.userDismissed) === 'true';

export const setUserDismissed = (): void => {
    safeSetItem(SESSION_KEYS.userDismissed, 'true');
};

const readShownLots = (): number[] => {
    const raw = safeGetItem(SESSION_KEYS.shownLots);
    if (!raw) {
        return [];
    }

    try {
        return JSON.parse(raw) as number[];
    } catch {
        return [];
    }
};

export const hasShownLot = (itemId: number): boolean => readShownLots().includes(itemId);

export const addShownLot = (itemId: number): void => {
    const shownLots = readShownLots();
    if (!shownLots.includes(itemId)) {
        safeSetItem(SESSION_KEYS.shownLots, JSON.stringify([...shownLots, itemId]));
    }
};

export const getSessionPromptCount = (): number => {
    const raw = safeGetItem(SESSION_KEYS.sessionPromptCount);
    return raw ? Number(raw) : 0;
};

export const incrementSessionPromptCount = (): void => {
    safeSetItem(SESSION_KEYS.sessionPromptCount, String(getSessionPromptCount() + 1));
};
